import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {Home, ShoppingBag, MapPin, FileText, Users, ShoppingCart, Shield, Grid, ChevronRight} from 'lucide-react';
import { lumi } from '../lib/lumi';

interface SitemapLink {
  name: string;
  path: string;
  isChild?: boolean; // 標記是否為子項目（文章/商品）
}

interface SitemapSection {
  icon: any;
  title: string;
  links: SitemapLink[];
}

const SitemapPage: React.FC = () => {
  const location = useLocation();
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'zh';
  const [articles, setArticles] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // 動態抓取資料（無限生成核心：limit 設為 1000）
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesResult, productsResult] = await Promise.all([
          lumi.entities.articles.list({
            filter: { status: 'published' },
            sort: { publishedAt: -1 },
            limit: 1000 
          }),
          lumi.entities.products.list({
            filter: { status: 'active' },
            sort: { createdAt: -1 },
            limit: 1000
          })
        ]);
        setArticles(articlesResult?.data || []);
        setProducts(productsResult?.data || []);
      } catch (error) {
        console.error('Failed to fetch sitemap data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const content = {
    zh: {
      title: '網站地圖 | ONGU Studio',
      description: '瀏覽 ONGU Studio 的完整網站結構，快速找到您需要的頁面',
      heading: '網站地圖',
      subtitle: '完整的網站導覽結構',
      sections: {
        main: '主要頁面',
        shop: '線上購物',
        store: '實體門市',
        lab: '醞玖製研中心',
        wholesale: 'B2B 批發',
        account: '會員中心',
        legal: '法律條款'
      },
      links: {
        home: '首頁',
        shopMain: '商品總覽',
        shopBeans: '咖啡豆',
        shopDripbag: '濾掛咖啡',
        shopGift: '禮盒',
        shopGear: '器具',
        cart: '購物車',
        checkout: '結帳',
        storeMain: '門市總覽',
        storeXinyi: '信義旗艦店',
        storeRenai: '仁愛店',
        labMain: '文章總覽',
        labNews: '最新消息',
        labGuide: '沖煮指南',
        labBeanstory: '豆款故事',
        labEvents: '活動資訊',
        wholesale: '批發服務',
        partners: '合作夥伴',
        roastLab: '烘豆資訊',
        member: '會員中心',
        memberOverview: '帳戶總覽',
        memberOrders: '訂單記錄',
        memberWishlist: '收藏清單',
        memberAddresses: '地址簿',
        memberSettings: '帳戶設定',
        privacy: '隱私政策',
        terms: '服務條款'
      }
    },
    en: {
      title: 'Sitemap | ONGU Studio',
      description: 'Browse the complete structure of ONGU Studio website and quickly find the page you need',
      heading: 'Sitemap',
      subtitle: 'Complete website navigation structure',
      sections: {
        main: 'Main Pages',
        shop: 'Online Shop',
        store: 'Physical Stores',
        lab: 'ONGU Lab',
        wholesale: 'B2B Wholesale',
        account: 'Member Center',
        legal: 'Legal Terms'
      },
      links: {
        home: 'Home',
        shopMain: 'Shop Overview',
        shopBeans: 'Coffee Beans',
        shopDripbag: 'Drip Bags',
        shopGift: 'Gift Sets',
        shopGear: 'Equipment',
        cart: 'Shopping Cart',
        checkout: 'Checkout',
        storeMain: 'Store Overview',
        storeXinyi: 'Xinyi Flagship',
        storeRenai: 'Renai Store',
        labMain: 'Articles Overview',
        labNews: 'News',
        labGuide: 'Brewing Guide',
        labBeanstory: 'Bean Stories',
        labEvents: 'Events',
        wholesale: 'Wholesale Services',
        partners: 'Partners',
        roastLab: 'Roast Lab',
        member: 'Member Center',
        memberOverview: 'Overview',
        memberOrders: 'Orders',
        memberWishlist: 'Wishlist',
        memberAddresses: 'Addresses',
        memberSettings: 'Settings',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service'
      }
    }
  };

  const t = content[currentLang];

  // Helper: 根據分類過濾並生成連結
  const getCategoryLinks = (
    items: any[], 
    category: string, 
    baseUrl: string, 
    type: 'article' | 'product'
  ): SitemapLink[] => {
    return items
      .filter(item => item.category === category)
      .map(item => ({
        name: currentLang === 'zh' ? item.name || item.title : (item.nameEn || item.titleEn || item.name || item.title),
        path: `/${currentLang}/${baseUrl}/${category}/${type === 'product' ? item._id + '/' : ''}${item.slug}`,
        isChild: true
      }));
  };

  const sitemapSections: SitemapSection[] = [
    {
      icon: Home,
      title: t.sections.main,
      links: [
        { name: t.links.home, path: `/${currentLang}` }
      ]
    },
    {
      icon: ShoppingBag,
      title: t.sections.shop,
      links: [
        { name: t.links.shopMain, path: `/${currentLang}/shop` },
        // 咖啡豆分類與商品
        { name: t.links.shopBeans, path: `/${currentLang}/shop/beans` },
        ...getCategoryLinks(products, 'beans', 'shop', 'product'),
        // 濾掛分類與商品
        { name: t.links.shopDripbag, path: `/${currentLang}/shop/dripbag` },
        ...getCategoryLinks(products, 'dripbag', 'shop', 'product'),
        // 禮盒分類與商品
        { name: t.links.shopGift, path: `/${currentLang}/shop/gift` },
        ...getCategoryLinks(products, 'gift', 'shop', 'product'),
        // 器具分類與商品
        { name: t.links.shopGear, path: `/${currentLang}/shop/gear` },
        ...getCategoryLinks(products, 'gear', 'shop', 'product'),
        
        { name: t.links.cart, path: `/${currentLang}/cart` },
        { name: t.links.checkout, path: `/${currentLang}/checkout` }
      ]
    },
    {
      icon: MapPin,
      title: t.sections.store,
      links: [
        { name: t.links.storeMain, path: `/${currentLang}/store` },
        { name: t.links.storeXinyi, path: `/${currentLang}/store/xinyi` },
        { name: t.links.storeRenai, path: `/${currentLang}/store/renai` }
      ]
    },
    {
      icon: FileText,
      title: t.sections.lab,
      links: [
        { name: t.links.labMain, path: `/${currentLang}/lab` },
        // News
        { name: t.links.labNews, path: `/${currentLang}/lab/news` },
        ...getCategoryLinks(articles, 'news', 'lab', 'article'),
        // Guide
        { name: t.links.labGuide, path: `/${currentLang}/lab/guide` },
        ...getCategoryLinks(articles, 'guide', 'lab', 'article'),
        // Bean Story
        { name: t.links.labBeanstory, path: `/${currentLang}/lab/beanstory` },
        ...getCategoryLinks(articles, 'beanstory', 'lab', 'article'),
        // Events
        { name: t.links.labEvents, path: `/${currentLang}/lab/events` },
        ...getCategoryLinks(articles, 'events', 'lab', 'article'),
      ]
    },
    {
      icon: Users,
      title: t.sections.wholesale,
      links: [
        { name: t.links.wholesale, path: `/${currentLang}/wholesale` },
        { name: t.links.partners, path: `/${currentLang}/partners` },
        { name: t.links.roastLab, path: `/${currentLang}/roast-lab` }
      ]
    },
    {
      icon: ShoppingCart,
      title: t.sections.account,
      links: [
        { name: t.links.member, path: `/${currentLang}/member` },
        { name: t.links.memberOverview, path: `/${currentLang}/member/overview` },
        { name: t.links.memberOrders, path: `/${currentLang}/member/orders` },
        { name: t.links.memberWishlist, path: `/${currentLang}/member/wishlist` },
        { name: t.links.memberAddresses, path: `/${currentLang}/member/addresses` },
        { name: t.links.memberSettings, path: `/${currentLang}/member/settings` }
      ]
    },
    {
      icon: Shield,
      title: t.sections.legal,
      links: [
        { name: t.links.privacy, path: `/${currentLang}/privacy` },
        { name: t.links.terms, path: `/${currentLang}/terms` }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t.title}</title>
        <meta name="description" content={t.description} />
        <link rel="canonical" href={`https://www.ongucoffee.com/${currentLang}/sitemap`} />
        <link rel="alternate" hreflang="zh-Hant" href="https://www.ongucoffee.com/zh/sitemap" />
        <link rel="alternate" hreflang="en" href="https://www.ongucoffee.com/en/sitemap" />
        <link rel="alternate" hreflang="x-default" href="https://www.ongucoffee.com/zh/sitemap" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-16">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center mb-6">
              <Grid size={40} className="text-gray-400 dark:text-gray-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-wider text-gray-800 dark:text-gray-200 mb-4">
              {t.heading}
            </h1>
            <div className="w-16 h-px bg-gray-400 dark:bg-gray-500 mx-auto mb-6"></div>
            <p className="text-gray-600 dark:text-gray-400 text-lg font-light">
              {t.subtitle}
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center text-gray-500 mb-8 font-light animate-pulse">
              {currentLang === 'zh' ? '正在載入完整網站結構...' : 'Loading complete site structure...'}
            </div>
          )}

          {/* Sitemap Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sitemapSections.map((section, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 transition-all duration-300 hover:shadow-lg h-full"
              >
                {/* Section Header */}
                <div className="flex items-center mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">
                  <section.icon size={20} className="text-gray-400 dark:text-gray-500 mr-3" />
                  <h2 className="text-lg font-light tracking-wide text-gray-800 dark:text-gray-200 uppercase">
                    {section.title}
                  </h2>
                </div>

                {/* Links */}
                <ul className="space-y-2">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <Link
                        to={link.path}
                        className={`
                          block text-sm transition-colors duration-200 py-1 flex items-center
                          ${link.isChild 
                            ? 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 pl-6 border-l border-gray-100 dark:border-gray-700 ml-1' 
                            : 'text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white font-normal'}
                        `}
                      >
                        {!link.isChild && (
                          <span className="inline-block w-1.5 h-1.5 bg-gray-300 dark:bg-gray-600 mr-3 rounded-full"></span>
                        )}
                        {link.name}
                        {link.isChild && (
                          <ChevronRight size={12} className="ml-1 opacity-50" />
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* XML Sitemap Link */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 px-6 py-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                {currentLang === 'zh' ? 'XML 網站地圖（供搜尋引擎使用）' : 'XML Sitemap (for search engines)'}
              </p>
              <a
                href="/sitemap.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-800 dark:text-gray-200 hover:text-gray-600 dark:hover:text-gray-400 font-light underline transition-colors duration-200"
              >
                sitemap.xml
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SitemapPage;